<script setup lang="ts">
import { computed, h, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NAvatar, NDropdown, NEllipsis, NPopover, NText } from 'naive-ui'
import defaultAvatar from '@/assets/avatar.jpg'
import { useAppStore, useAuthStore, useClassroomStore, useCreditStore, useUserStore } from '@/store'
import { transformMinioUrl } from '@/utils/functions'
import CreditDisplay from '@/components/common/CreditDisplay.vue'
import { t } from '@/locales'
import { AppSvgEIcon, SvgIcon } from '@/components/common'
import englishIcon from '@/assets/classroom-v2/english.svg'
import englishIconDark from '@/assets/classroom-v2/englishDark.svg'
import vietnameseIcon from '@/assets/classroom-v2/vietnamese.svg'
import vietnameseIconDark from '@/assets/classroom-v2/vietnameseDark.svg'
import FormSetPassword from '@/components/layout/sider/FormSetPassword.vue'
import FormChangePassword from '@/components/layout/sider/FormChangePassword.vue'
import FormUserProfile from '@/components/layout/sider/FormUserProfile.vue'
import FormClassInfo from '@/components/layout/sider/FormClassInfo.vue'
import { isUserRole } from '@/constants/role'
import { useIframeTheme } from '@/composables/useIframeTheme'
import { renderColor } from '@/composables/useRenderColor'
import FromDetailWorkspace from '@/views/workspace-settings/FromDetailWorkspace.vue'
import { useWebSocket } from '@/composables/useWebSocket'
import { getListClassroomForStudent } from '@/api/classroom'
import { getNotificationDetail, getUserNotifications, getUserNotificationsCount, readUserNotification } from '@/api/notiConfig'
import FormJoinClass from '@/components/layout/sider/FormJoinClass.vue'

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
  isDrawer: {
    type: Boolean,
    default: false,
  },
})
const show = ref<boolean>(false)
const userStore = useUserStore()
const router = useRouter()
const creditStore = useCreditStore()
const authStore = useAuthStore()
const appStore = useAppStore()
const classroomStore = useClassroomStore()
const route = useRoute()

const { sendThemeToIframes } = useIframeTheme()

const userInfo = computed(() => userStore.userInfo)
const userRole = computed(() => userStore.role)
const basicCredit = computed(() => creditStore.basicCreditBalance)
const advancedCredit = computed(() => creditStore.advancedCreditBalance)
const currentClassroom = computed(() => classroomStore.getCurrentClassroom)
const listClassroom = computed(() => classroomStore.getClassrooms)

// Password forms
const showSetPassword = ref<boolean>(false)
const showChangePassword = ref<boolean>(false)

// User profile form
const showUserProfile = ref<boolean>(false)

// Class info form
const showClassInfo = ref<boolean>(false)
const showDetailWorkspace = ref<boolean>(false)
const theme = computed(() => appStore.theme)
const language = computed(() => appStore.language)
const showJoinClass = ref<boolean>(false)

const renderCustomHeader = () => {
  return h(
    'div',
    {
      class: 'p-2 flex items-center cursor-pointer',
      onClick: handleViewProfile,
    },
    [
      h(NAvatar, {
        round: true,
        style: 'margin-right: 12px;',
        class: 'h-8 w-8',
        objectFit: 'cover',
        src: userInfo.value.icon_url ? transformMinioUrl(userInfo.value.icon_url) : defaultAvatar,
      }),
      h('div', null, [
        h('div', null, [
          h(NText, { depth: 2 }, {
            default: () => userInfo.value.fullName || '',
          }),
        ]),
        h('div', { style: 'font-size: 12px;' }, [
          h(NText, { depth: 3 }, { default: () => userInfo?.value?.email }),
        ]),
      ]),
    ],
  )
}

const createImgIcon = (src: string, alt: string, classname: any = '') => () =>
  h('img', {
    src,
    alt,
    class: `${classname} w-5 h-5 object-contain`,
  })

const dropdownOptions = computed(() => [
  {
    key: 'header',
    type: 'render',
    render: renderCustomHeader,
  },
  {
    type: 'divider',
    key: 'd1',
  },
  {
    label: t('settings.userManagementDropdown.workspaceInfo'),
    key: 'workspace-settings',
    icon: () => h(AppSvgEIcon, { name: 'element-4', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
    show: isUserRole(userRole.value, 'ADMIN'),
  },
  {
    label: t('settings.userManagementDropdown.classInfo'),
    key: 'class-info',
    icon: () => h(SvgIcon, { icon: 'streamline-flex:school', size: '20' }),

    show: isUserRole(userRole.value, 'TEACHER'),
  },
  {
    label: t('auth.joinClass'),
    key: 'join-class',
    icon: () => h(AppSvgEIcon, { name: 'element-4', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
    show: isUserRole(userRole.value, 'STUDENT'),
  },
  {
    label: userStore?.isFirstLogin ? t('auth.setup_password') : t('auth.change_password'),
    key: 'set-password',
    icon: () => h(AppSvgEIcon, { name: 'security-safe', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
  },
  // {
  //   label: t('settings.userManagementDropdown.systemSettings'),
  //   key: 'system-settings',
  //   icon: () => h(AppSvgEIcon, { name: 'setting', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
  //   children: [
  //     {
  //       label: t('settings.userManagementDropdown.textbook'),
  //       key: 'textbook',
  //     },
  //     {
  //       label: t('settings.userManagementDropdown.teacherBook'),
  //       key: 'teacher-book',
  //     },
  //     {
  //       label: t('settings.userManagementDropdown.toolManagement'),
  //       key: 'tool-management',
  //     },
  //   ],
  // },
  // },
  {
    label: t('planAndStorage.title'),
    key: 'plan-storage',
    icon: () => h(AppSvgEIcon, { name: 'database', size: 24, color: theme.value === 'dark' ? '#ffffffd1' : '#333639' }),
  },
  {
    label: 'English',
    key: 'english',
    icon: createImgIcon(theme.value === 'dark' ? englishIconDark : englishIcon, 'English flag'),
    show: language.value === 'en-US',
  },
  {
    label: 'Tiếng Việt',
    key: 'vietnamese',
    icon: createImgIcon(theme.value === 'dark' ? vietnameseIconDark : vietnameseIcon, 'Vietnamese flag'),
    show: language.value === 'vi-VN',
  },
  {
    label: t('common.light'),
    key: 'light',
    icon: () => h(SvgIcon, {
      icon: 'mynaui:cloud-sun',
      size: '20',
    }),
    show: theme.value === 'light',
  },
  {
    label: t('common.dark'),
    key: 'dark',
    icon: () => h(SvgIcon, {
      icon: 'solar:moon-fog-outline',
      size: '20',
    }),
    show: theme.value === 'dark',
  },

  {
    label: () =>
      h('span', { class: 'text-[#F14933]' }, t('settings.userManagementDropdown.logout')),
    key: 'logout',
    icon: () => h(AppSvgEIcon, { name: 'login', size: 24, color: '#F14933' }),
  },
])

const handleViewProfile = () => {
  showUserProfile.value = true
}

const onCloseUserProfile = (reload: boolean) => {
  showUserProfile.value = false
  if (reload) {
    // Handle success case if needed - refresh user info
  }
}

const handleClassInfo = () => {
  showClassInfo.value = true
}

const handleWorkspaceInfo = () => {
  router.push({
    name: 'classroom.workspace',
  })
}

const onCloseClassInfo = (reload: boolean) => {
  showClassInfo.value = false
  if (reload) {
    // Handle success case if needed
  }
}

const handleSetPassword = () => {
  if (userStore?.isFirstLogin)
    showSetPassword.value = true

  else
    showChangePassword.value = true
}

const onCloseSetPassword = async (reload: boolean, info: any) => {
  showSetPassword.value = false
  if (reload)
    await authStore.logout()
    // await router.push({ name: 'login' })
}

const onCloseChangePassword = async (reload: boolean) => {
  showChangePassword.value = false
  if (reload) {
    // Handle success case if needed
    await authStore.logout()
    // await router.push({ name: 'login' })
  }
}

// const handleSystemSettings = (key: string) => {
//   console.log(key)
//   router.push({ name: 'classroom.teacher.system-settings', query: { tab: key } })
// }

const handleLogout = async () => {
  await authStore.logout()
  // await router.push({ name: 'login' })
}

const handleSetLanguage = (value: any) => {
  appStore.setLanguage(value)
}

function handleThemeChange(themeKey: any) {
  appStore.setTheme(themeKey)
  sendThemeToIframes(themeKey)
}

const handlePackageAndStorage = () => {
  if (isUserRole(userRole.value, 'ADMIN', 'TEACHER'))
    router.push({ name: 'classroom.teacher.plan-storage' })

  else
    router.push({ name: 'classroom.student.plan-storage' })
}

const handleJoinClass = () => {
  showJoinClass.value = true
}

const onCloseJoinClass = () => {
  showJoinClass.value = false
}

const onSuccessJoinClass = (reload: any) => {
  showJoinClass.value = false
}

const handleSelect = (key: string) => {
  switch (key) {
    case 'class-info':
      handleClassInfo()
      break
    case 'workspace-settings':
      handleWorkspaceInfo()
      break
    case 'set-password':
      handleSetPassword()
      break
	  // case 'textbook':
    //   handleSystemSettings('textbook')
    //   break
    // case 'teacher-book':
    //   handleSystemSettings('teacher-book')
    //   break
    // case 'tool-management':
    //   handleSystemSettings('tool-management')
    //   break
    case 'logout':
      handleLogout()
      break
    case 'english':
      handleSetLanguage('vi-VN')
      break
    case 'vietnamese':
      handleSetLanguage('en-US')
      break
    case 'light':
      handleThemeChange('dark')
      break
    case 'dark':
      handleThemeChange('light')
      break
    case 'plan-storage':
      handlePackageAndStorage()
      break
    case 'join-class':
      handleJoinClass()
      break
  }
}

interface NotificationItem {
  id: number
  title: string
  message?: string
  timeText?: string
  redirect?: string | null
  read: boolean
  createdAt: number
}

const showNotiPopover = ref(false)
const notifications = ref<NotificationItem[]>([])

const notificationPage = ref(0)
const notificationPageSize = 20
const notificationLoading = ref(false)
const notificationHasMore = ref(true)
const notificationError = ref<string | null>(null)

const unreadCount = ref(0)
const studentClassrooms = ref<any[]>([])

const refreshUnreadCount = async () => {
  try {
    const res = await getUserNotificationsCount()
    unreadCount.value = Number((res as any)?.data?.count ?? (res as any)?.data ?? (res as any)?.count ?? 0)
  }
  catch (e) {
    console.error('Error fetching notification count:', e)
  }
}

const nowTick = ref(Date.now())
let nowTickInterval: number | undefined

const formatRelativeTime = (ts: number) => {
  const diff = Math.max(0, nowTick.value - ts)
  const sec = Math.floor(diff / 1000)
  if (sec < 60)
    return t('common.justNow') || 'vừa xong'

  const min = Math.floor(sec / 60)
  if (min < 60)
    return `${min} ${t('common.minutes') || 'phút'}${t('common.ago') || ' trước'}`

  const hour = Math.floor(min / 60)
  if (hour < 24)
    return `${hour} ${t('common.hours') || 'giờ'}${t('common.ago') || ' trước'}`

  const day = Math.floor(hour / 24)
  return `${day} ${t('common.days') || 'ngày'}${t('common.ago') || ' trước'}`
}

interface UserNotificationApiItem {
  id: number
  title: string
  message?: string
  is_read?: boolean
  created_at?: string
  url?: string | null
}

interface UserNotificationApiResponse {
  success: boolean
  data: UserNotificationApiItem[]
}

const normalizeNotifications = (payload: unknown): NotificationItem[] => {
  const res = payload as Partial<UserNotificationApiResponse>
  const list = Array.isArray(res?.data) ? res.data : []

  return list
    .map((item) => {
      const createdAtMs = item?.created_at ? Date.parse(item.created_at) : Date.now()
      return {
        id: item.id,
        title: item.title || '',
        message: item.message,
        timeText: item?.created_at ? formatRelativeTime(createdAtMs) : undefined,
        redirect: item.url ?? null,
        read: Boolean(item.is_read),
        createdAt: createdAtMs,
      } satisfies NotificationItem
    })
    .filter(i => i.title)
    .sort((a, b) => b.createdAt - a.createdAt)
}

// Merge notifications and avoid duplicates by ID
const mergeNotifications = (existing: NotificationItem[], newItems: NotificationItem[]): NotificationItem[] => {
  const byId = new Map<number, NotificationItem>()

  // Prefer existing read state if already present
  existing.forEach((n) => {
    byId.set(n.id, n)
  })

  newItems.forEach((n) => {
    const prev = byId.get(n.id)
    if (prev)
      byId.set(n.id, { ...n, read: prev.read || n.read })
    else
      byId.set(n.id, n)
  })

  return Array.from(byId.values())
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 50)
}

const pushNotifications = (items: NotificationItem[]) => {
  if (items.length === 0)
    return

  notifications.value = mergeNotifications(notifications.value, items)
}

const fetchNotifications = async (isLoadMore = false) => {
  if (notificationLoading.value || (!isLoadMore && !notificationHasMore.value))
    return

  notificationLoading.value = true
  notificationError.value = null

  try {
    const response = await getUserNotifications()
    const newItems = normalizeNotifications(response)

    if (isLoadMore) {
      // For load more, merge with existing notifications
      pushNotifications(newItems)
    }
    else {
      // For initial load, replace existing notifications
      notifications.value = newItems
    }

    // Update pagination state
    notificationPage.value += 1
    notificationHasMore.value = newItems.length >= notificationPageSize
  }
  catch (error) {
    console.error('Error fetching notifications:', error)
    notificationError.value = 'Failed to load notifications. Please try again.'
  }
  finally {
    notificationLoading.value = false
  }
}

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  if (!target)
    return

  const { scrollTop, scrollHeight, clientHeight } = target
  const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 100 // 100px from bottom

  if (isNearBottom && !notificationLoading.value && notificationHasMore.value)
    fetchNotifications(true) // Load more
}

// const markAllReadLocal = () => {
//   notifications.value = notifications.value.map(n => ({ ...n, read: true }))
// }

const selectedNotification = ref<NotificationItem | null>(null)

const handleClickNotification = async (item: NotificationItem) => {
  // Update UI immediately for better UX
  const originalRead = item.read
  item.read = true

  try {
    // Call API to mark as read
    if (!originalRead && unreadCount.value > 0)
      unreadCount.value -= 1

    await readUserNotification(item.id)

    // Then fetch the notification details
    const response = await getNotificationDetail(item.id)

    // Update the notification in the list with any new data
    const updatedNotification = {
      ...item,
      ...response?.data,
      read: true,
      timeText: response?.data?.created_at ? formatRelativeTime(Date.parse(response.data.created_at)) : item.timeText,
    }

    const index = notifications.value.findIndex(n => n.id === item.id)
    if (index !== -1)
      notifications.value[index] = updatedNotification

    // Set the selected notification to show detail view
    selectedNotification.value = updatedNotification
  }
  catch (error) {
    console.error('Error handling notification click:', error)
    // Revert read state if API calls fail
    item.read = originalRead
  }
}

watch(showNotiPopover, async (val: boolean) => {
  if (val) {
    // Only open and load notifications; do not mark all read automatically
    if (notifications.value.length === 0 && !notificationLoading.value)
      await fetchNotifications(false)
    await refreshUnreadCount()
  }
  else {
    selectedNotification.value = null
    await refreshUnreadCount()
  }
})

const wsUrl = `${import.meta.env.VITE_GLOB_SOCKET_URL}/agents-service/ws`
const { connect: connectNotificationScheduleBannerWs } = useWebSocket(wsUrl, {
  token: authStore.token,
  onMessage: (data: any) => {
    // Socket payload could be:
    // { email, type, data: { id, title, message?, count, created_at, url? } }
    const payload = data as any

    const socketItem = payload?.data

    if (socketItem?.id && socketItem?.title) {
      const createdAtMs = socketItem?.created_at ? Date.parse(socketItem.created_at) : Date.now()
      const item: NotificationItem = {
        id: Number(socketItem.id),
        title: String(socketItem.title || ''),
        message: socketItem?.message,
        timeText: socketItem?.created_at ? formatRelativeTime(createdAtMs) : undefined,
        redirect: socketItem?.url ?? null,
        read: false,
        createdAt: createdAtMs,
      }

      // push to top immediately
      notifications.value = mergeNotifications([item], notifications.value)

      // each pushed item counts as 1 unread

      unreadCount.value += 1

      return
    }

    // Fallback to previous payload shape
    const items = normalizeNotifications(data)
    if (items.length) {
      pushNotifications(items)
      refreshUnreadCount()
    }
  },
  onError: () => {
    console.warn('[agents-service][ws] error')
  },
})

const getStudentClassrooms = async () => {
  try {
    const { data } = await getListClassroomForStudent()
    if (data?.success)
      studentClassrooms.value = data.data || []
    else if (Array.isArray(data?.data))
      studentClassrooms.value = data.data
  }
  catch (error) {
    console.error(error)
  }
}

const studentClassroomOptions = computed(() => {
  return listClassroom.value.map((item: any) => ({
    label: item.name,
    key: item.id,
    disabled: currentClassroom.value.id === item.id,
  }))
})

const handleSelectStudentClassroom = (key: string | number) => {
  const selected = listClassroom.value.find((item: any) => item.id === key)
  if (selected) {
    classroomStore.setCurrentClassroom(selected)
    router.push({
      name: 'classroom.student.board',
      params: {
        classroomId: key,
      },
    })
  }
}

const menuProps = (
  option,
  options,
) => {
  return {
    class: 'w-[240px]',
  }
}

onMounted(async () => {
  if (isUserRole(userRole.value, 'STUDENT'))
    await getStudentClassrooms()

  connectNotificationScheduleBannerWs()
  nowTickInterval = window.setInterval(() => {
    nowTick.value = Date.now()
    // Update relative time text without changing order/read state
    notifications.value = notifications.value.map(n => ({
      ...n,
      timeText: formatRelativeTime(n.createdAt),
    }))
  }, 60_000)

  // Initial fetch of unread count
  await refreshUnreadCount()
})

onUnmounted(() => {
  if (nowTickInterval)
    window.clearInterval(nowTickInterval)
})
</script>

<template>
  <div class="space-y-2 overflow-y-auto max-h-[40vh]" :class="collapsed ? 'py-4 px-2' : 'p-2'">
    <!-- User info and credits - hidden in drawer mode -->
    <div v-if="!isDrawer" class="space-y-3 " :class="collapsed ? 'py-3' : 'p-1 border bg-[#F7F6F7] dark:bg-gray-800  rounded-xl'">
      <div class="flex items-center gap-2  min-w-0" :class="!collapsed ? 'w-[240px] justify-between' : 'w-full justify-center'">
        <NDropdown trigger="hover" :options="dropdownOptions" placement="top-start" class="!p-0 flex-1 min-w-0" @select="handleSelect">
          <div class="flex items-center gap-2 cursor-pointer min-w-0" :class="!collapsed ? '' : 'justify-center'">
            <div class="w-8 h-8 rounded-full flex-shrink-0">
              <img
                :src="userInfo.icon_url ? transformMinioUrl(userInfo.icon_url) : defaultAvatar" alt="User Avatar"
                class="w-full h-full rounded-full object-cover border-2 border-white dark:border-gray-800"
              >
            </div>
            <NEllipsis v-if="!collapsed" :tooltip="false" class="font-semibold text-base min-w-0">
              <span class="truncate">{{ userInfo.fullName }}</span>
            </NEllipsis>
          </div>
        </NDropdown>

        <div v-if="!collapsed">
          <NPopover
            v-model:show="showNotiPopover"
            trigger="click"
            placement="top-end"
            :content-style="{ padding: '0' }"
            style="padding: 0 !important; border-radius: 16px; background-color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"
            :show-arrow="false"
          >
            <template #trigger>
              <div class="relative cursor-pointer p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 flex-shrink-0">
                <SvgIcon icon="ion:notifications-outline" size="20" />
                <div
                  v-if="unreadCount > 0"
                  class="absolute -top-1 -right-1 min-w-4 h-4 px-1.5 bg-gradient-to-r from-red-500 to-pink-500 text-white text-[11px] leading-5 rounded-full flex items-center justify-center shadow-md ring-2 ring-white dark:ring-gray-800 animate-bounce"
                >
                  {{ unreadCount > 99 ? '99+' : unreadCount }}
                </div>
              </div>
            </template>
            <div class="w-[300px] max-h-[400px] flex flex-col bg-white dark:bg-gray-800 rounded-lg">
              <div class="p-4 font-semibold text-base border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <template v-if="selectedNotification">
                  <div
                    class="cursor-pointer flex items-center justify-center"
                    @click="selectedNotification = null"
                  >
                    <SvgIcon icon="ion:chevron-back" size="18" />
                  </div>
                  <div class="flex-1 truncate">
                    {{ t('common.notifications') }}
                  </div>
                </template>
                <template v-else>
                  {{ t('common.notifications') }}
                </template>
              </div>

              <div v-if="selectedNotification" class="flex-1 overflow-y-auto p-4 space-y-2">
                <div class="text-base font-semibold text-gray-800 dark:text-gray-100">
                  {{ selectedNotification.title }}
                </div>
                <div v-if="selectedNotification.timeText" class="text-xs text-purple-500">
                  {{ selectedNotification.timeText }}
                </div>
                <div class="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">
                  {{ selectedNotification.message || '' }}
                </div>
              </div>

              <div v-else-if="notifications.length === 0" class="flex-1 flex items-center justify-center p-8 text-gray-500">
                {{ t('common.noData') }}
              </div>
              <div v-else class="flex-1 overflow-y-auto p-2 space-y-1" @scroll="handleScroll">
                <div
                  v-for="item in notifications"
                  :key="item.id"
                  class="p-2 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  :class="item.read ? 'opacity-70' : 'bg-purple-50 dark:bg-purple-900/20'"
                  @click="handleClickNotification(item)"
                >
                  <NEllipsis :line-clamp="2" class="text-sm text-gray-800 dark:text-gray-200" :class="item.read ? 'font-medium' : 'font-semibold'">
                    {{ item.title }}
                  </NEllipsis>
                  <div v-if="item.timeText" class="text-xs text-purple-500 mt-1" :class="item.read ? 'opacity-70' : 'opacity-100'">
                    {{ item.timeText }}
                  </div>
                </div>
              </div>
            </div>
          </NPopover>
        </div>
      </div>
      <CreditDisplay
        :basic-credit="basicCredit"
        :advanced-credit="advancedCredit"
        :show-card="false"
        :is-footer="true"
        :collapsed="collapsed"
      />
    </div>
    <div v-if="isUserRole(userRole, 'ADMIN', 'TEACHER')">
      <div
        v-if="!collapsed"
        class="rounded-full p-2 flex items-center" :style="{
          backgroundColor: renderColor(0.1),
        }"
        :class="!collapsed ? 'justify-between' : 'justify-center'"
        @click="showDetailWorkspace = true"
      >
        <div class="flex gap-2 items-center">
          <div>
            <img
              :src="userStore.workSpaceLogo ? transformMinioUrl(userStore.workSpaceLogo) : defaultAvatar" alt="workspace Logo"
              class="min-w-6 w-6 h-6 rounded-full object-cover"
            >
          </div>
          <NEllipsis v-if="!collapsed" :line-clamp="1" class="font-semibold">
            {{ userStore.workSpace }}
          </NEllipsis>
        </div>
        <div v-if="!collapsed" class="w-[6px] h-[6px] bg-green-600 rounded-full mr-2" />
      </div>
    </div>
    <div v-if="isUserRole(userRole, 'STUDENT')">
      <NDropdown
        v-if="!collapsed"
        trigger="click"
        :options="studentClassroomOptions"
        class=""
        placement="top-start"
        :show-arrow="false"
        :menu-props="menuProps"
        :disabled="studentClassroomOptions.length <= 1"
        @select="handleSelectStudentClassroom"
      >
        <div
          class="rounded-full py-2 px-4 flex items-center cursor-pointer hover:opacity-80 transition-opacity"
          :style="{
            backgroundColor: renderColor(0.1),
          }"
          :class="!collapsed ? 'justify-between' : 'justify-center'"
        >
          <div class="truncate flex-1">
            {{ currentClassroom?.name }}
          </div>
          <div v-if="studentClassroomOptions.length > 1" class="ml-1 flex">
            <AppSvgEIcon name="refresh" :size="24" />
          </div>
        </div>
      </NDropdown>
    </div>
  </div>

  <!-- Password Forms -->
  <FormSetPassword
    v-if="showSetPassword"
    :show="showSetPassword"
    :email="userInfo.email"
    @on-close="onCloseSetPassword"
  />
  <FormChangePassword
    v-if="showChangePassword"
    :show="showChangePassword"
    @on-close="onCloseChangePassword"
  />
  <FormUserProfile
    v-if="showUserProfile"
    :show="showUserProfile"
    @on-close="onCloseUserProfile"
  />
  <FormClassInfo
    v-if="showClassInfo"
    :show="showClassInfo"
    @on-close="onCloseClassInfo"
  />
  <FromDetailWorkspace
    v-if="showDetailWorkspace"
    :show="showDetailWorkspace"
    @on-close="showDetailWorkspace = false"
  />
  <FormJoinClass
    v-if="showJoinClass"
    :show="showJoinClass"
    @on-close="onCloseJoinClass"
    @on-success="onSuccessJoinClass"
  />
</template>
